import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtDate } from "@/lib/api";
import { NAVY, TEAL, GREEN, AMBER, RED, SLATE, TEAL_LT, rs, loadImage, drawHeader, drawFooter, drawPill } from "@/lib/pdfBrand";

const PILL = {
  Paid: [GREEN, [236, 253, 245]],
  Overdue: [RED, [254, 242, 242]],
  "Due Soon": [AMBER, [255, 251, 235]],
  Upcoming: [SLATE, [241, 245, 249]],
};

function loanStatusBadge(doc, x, y, status) {
  const up = (status || "").toString().toUpperCase();
  const closed = up === "CLOSED";
  drawPill(doc, x, y, up, closed ? SLATE : GREEN, closed ? [241, 245, 249] : [236, 253, 245], 8);
}

// Premium customer Loan Statement — matches approved reference design.
// Financial-safety rule: NO per-EMI principal/interest columns in the customer schedule.
export async function generateStatementPdf(data) {
  const { loan, customer, schedule = [], generated_at } = data;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const mascot = await loadImage("/brand/zrope-mascot.png");
  drawHeader(doc, { rightTitle: "LOAN STATEMENT", rightSub: `Generated on: ${fmtDate(generated_at)}`, mascot });

  // greeting
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`Dear ${customer?.name || "Customer"},`, 40, 128);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  doc.text("Here is your loan statement summary and repayment schedule.", 40, 144);

  // subtle penguin watermark beside loan info
  if (mascot && doc.setGState) {
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.addImage(mascot, "PNG", W - 250, 150, 210, 220);
    doc.setGState(new doc.GState({ opacity: 1 }));
  }

  // loan information
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...TEAL);
  doc.text("LOAN INFORMATION", 40, 172);
  const paid = Math.max(0, (loan.tenure_months || 0) - (loan.remaining_emi || 0));
  const info = [
    ["Loan Name", `${loan.lender} — ${loan.loan_type}`],
    ["Loan Account Number", loan.account_number || "-"],
    ["Loan Amount", rs(loan.principal)],
    ["Outstanding Amount", rs(loan.outstanding)],
    ["EMI Amount", rs(loan.emi_amount)],
    ["Interest Rate", `${loan.interest_rate}% p.a.`],
    ["Loan Tenure", `${loan.tenure_months} Months`],
    ["EMI Start Date", fmtDate(loan.start_date)],
    ["Loan End Date", fmtDate(loan.end_date)],
    ["EMIs Paid", String(paid)],
    ["EMIs Remaining", String(loan.remaining_emi)],
    ["Loan Status", loan.status],
  ];
  let y = 192;
  doc.setFontSize(9);
  info.forEach(([k, v]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...SLATE);
    doc.text(k, 56, y);
    if (k === "Loan Status") {
      loanStatusBadge(doc, 210, y - 10, v);
    } else {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...NAVY);
      doc.text(String(v), 210, y);
    }
    y += 19;
  });

  // repayment schedule header + summary
  const sy = y + 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...TEAL);
  doc.text("REPAYMENT SCHEDULE", 40, sy);
  const cPaid = schedule.filter((r) => r.status === "Paid").length;
  const cUp = schedule.filter((r) => r.status === "Upcoming" || r.status === "Due Soon").length;
  const cOd = schedule.filter((r) => r.status === "Overdue").length;
  let sx = W - 40;
  [[`${cOd} Overdue`, RED], [`${cUp} Upcoming`, SLATE], [`${cPaid} Paid`, GREEN]].forEach(([txt, col]) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(col[0], col[1], col[2]);
    doc.text(txt, sx, sy, { align: "right" });
    sx -= doc.getTextWidth(txt) + 18;
  });

  // schedule table — only authorized columns (no principal/interest split)
  autoTable(doc, {
    startY: sy + 12,
    head: [["#", "Due Date", "EMI Amount", "Outstanding Balance", "Status", "Paid On"]],
    body: schedule.map((r) => [
      r.emi_no, fmtDate(r.due_date), rs(r.emi), rs(r.remaining_principal), r.status, r.paid_date ? fmtDate(r.paid_date) : "-",
    ]),
    theme: "grid",
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 5, textColor: NAVY, lineColor: [233, 237, 243], lineWidth: 0.5 },
    alternateRowStyles: { fillColor: [247, 249, 252] },
    margin: { left: 40, right: 40, top: 40, bottom: 62 },
    showHead: "everyPage",
    didParseCell: (d) => { if (d.section === "body" && d.column.index === 4) d.cell.text = [""]; },
    didDrawCell: (d) => {
      if (d.section === "body" && d.column.index === 4) {
        const s = schedule[d.row.index]?.status;
        const [fg, bg] = PILL[s] || [SLATE, [241, 245, 249]];
        drawPill(doc, d.cell.x + 5, d.cell.y + (d.cell.height - 15) / 2, s, fg, bg, 7.5);
      }
    },
  });

  // thank-you note if room on last page
  const finalY = doc.lastAutoTable.finalY;
  if (finalY < H - 132) {
    const bY = finalY + 18;
    doc.setDrawColor(...TEAL_LT);
    doc.setFillColor(240, 253, 250);
    doc.roundedRect(40, bY, W - 80, 54, 10, 10, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text("Thank you for being a responsible borrower.", 60, bY + 22);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SLATE);
    doc.text("For any queries, visit our Support section in the app or website.", 60, bY + 40);
  }

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    drawFooter(doc, { logo: true, line2: "Support: support@zrope.in   |   www.zrope.in", page: i, pages });
  }
  doc.save(`ZRoPe-Statement-${(loan.lender || "loan").replace(/\s+/g, "-")}.pdf`);
}

// Admin EMI schedule export (principal/interest allowed for internal/admin use).
export async function generateSchedulePdf(data) {
  const { loan = {}, schedule = [], summary = {} } = data;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const mascot = await loadImage("/brand/zrope-mascot.png");
  drawHeader(doc, { rightTitle: "EMI SCHEDULE", rightSub: `Generated on: ${fmtDate(new Date().toISOString())}`, mascot });

  let y = 124;
  doc.setTextColor(...NAVY);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`${loan.lender || ""} — ${loan.loan_type || ""}`, 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...SLATE);
  y += 16;
  doc.text(`Customer: ${loan.customer_name || "-"}    |    EMIs: ${summary.total ?? schedule.length}    Paid: ${summary.paid ?? "-"}    Overdue: ${summary.overdue ?? "-"}`, 40, y);

  autoTable(doc, {
    startY: y + 14,
    head: [["#", "Due Date", "Principal", "Interest", "EMI", "Outstanding", "Status", "Paid On"]],
    body: schedule.map((r) => [
      r.emi_no, fmtDate(r.due_date), rs(r.principal), rs(r.interest),
      rs(r.emi), rs(r.remaining_principal), r.status, r.paid_date ? fmtDate(r.paid_date) : "-",
    ]),
    headStyles: { fillColor: NAVY, textColor: 255, fontStyle: "bold", fontSize: 8 },
    styles: { fontSize: 8, cellPadding: 4 },
    alternateRowStyles: { fillColor: [246, 248, 251] },
    margin: { left: 40, right: 40, top: 40, bottom: 62 },
    showHead: "everyPage",
    didParseCell: (d) => {
      if (d.section === "body" && d.column.index === 6) {
        const s = d.cell.raw;
        if (s === "Paid") d.cell.styles.textColor = GREEN;
        else if (s === "Overdue") d.cell.styles.textColor = RED;
        else if (s === "Due Soon") d.cell.styles.textColor = AMBER;
      }
    },
  });

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    drawFooter(doc, { logo: true, line2: "Support: support@zrope.in   |   www.zrope.in", page: i, pages });
  }
  doc.save(`ZRoPe-EMI-Schedule-${(loan.lender || "loan").replace(/\s+/g, "-")}.pdf`);
}
